import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "@emotion/styled";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useSpace } from "../../../contexts/SpaceContext";
import ReusableTable from "../../../components/Common/ReusableTable";
import WidgetCard from "./WidgetCard";
import "../../../Styles/Custom.css";

import {
  Alert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Paper as MuiPaper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Language as WebsiteIcon,
  SmartToy as BotIcon,
} from "@mui/icons-material";

import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

// GraphQL Queries and Mutations
const GET_ALL_WIDGETS = gql`
  query GetAllWidgets($spaceId: ID) {
    getAllWidgets(spaceId: $spaceId) {
      id
      business_details {
        name
        display_name
        business_type
        website_url
      }
      script_id
    }
  }
`;

const DELETE_WIDGET = gql`
  mutation DeleteWidget($id: ID!) {
    deleteWidget(id: $id)
  }
`;

const DELETE_WIDGETS = gql`
  mutation DeleteWidgets($ids: [ID!]!) {
    deleteWidgets(ids: $ids)
  }
`;

// Business type mapping
const getBusinessTypeName = (type) => {
  const businessTypes = {
    1: "E-commerce",
    2: "Service Business",
    3: "Educational",
    4: "Healthcare",
    5: "Technology",
    6: "Other",
  };
  return businessTypes[type] || "General";
};

function EnhancedTable() {
  const location = useLocation();
  const { currentSpace } = useSpace();

  // State management
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewStyle, setViewStyle] = useState("List");
  const [searchQuery, setSearchQuery] = useState("");
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [singleDeleteDialog, setSingleDeleteDialog] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Get spaceId from URL params or use current space
  const queryParams = new URLSearchParams(location.search);
  const spaceId = queryParams.get("spaceId") || currentSpace?.id;

  // Fetch widgets with space filtering and cache management
  const { loading, error, data, refetch } = useQuery(GET_ALL_WIDGETS, {
    variables: { spaceId: spaceId },
    skip: !spaceId,
    fetchPolicy: "cache-and-network",
  });

  // Delete mutations
  const [deleteWidget] = useMutation(DELETE_WIDGET, {
    update(cache, { data: mutationData }) {
      if (mutationData?.deleteWidget && spaceId) {
        // Update cache by removing the deleted widget
        const existingData = cache.readQuery({
          query: GET_ALL_WIDGETS,
          variables: { spaceId: spaceId },
        });

        if (existingData?.getAllWidgets) {
          cache.writeQuery({
            query: GET_ALL_WIDGETS,
            variables: { spaceId: spaceId },
            data: {
              getAllWidgets: existingData.getAllWidgets.filter(
                (widget) => widget.id !== deleteTargetId
              ),
            },
          });
        }
      }
    },
  });

  const [deleteWidgets] = useMutation(DELETE_WIDGETS, {
    update(cache, { data: mutationData }) {
      if (mutationData?.deleteWidgets && spaceId) {
        // Update cache by removing the deleted widgets
        const existingData = cache.readQuery({
          query: GET_ALL_WIDGETS,
          variables: { spaceId: spaceId },
        });

        if (existingData?.getAllWidgets) {
          cache.writeQuery({
            query: GET_ALL_WIDGETS,
            variables: { spaceId: spaceId },
            data: {
              getAllWidgets: existingData.getAllWidgets.filter(
                (widget) => !selected.includes(widget.id)
              ),
            },
          });
        }
      }
    },
  });

  // Refetch data when component mounts or spaceId changes
  useEffect(() => {
    if (spaceId && refetch) {
      console.log("🔄 Refreshing widget list for space:", spaceId);
      refetch();
    }
  }, [spaceId, refetch]);

  // Also refetch when navigating back from creation
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get("refresh") === "true" && refetch) {
      console.log("🔄 Refreshing widget list after creation");
      refetch();
      // Clean up URL by removing refresh parameter
      urlParams.delete("refresh");
      const newUrl = `${location.pathname}${
        urlParams.toString() ? "?" + urlParams.toString() : ""
      }`;
      window.history.replaceState({}, "", newUrl);
    }
  }, [location.search, refetch]);

  // Transform widget data into table rows
  const processedWidgets = useMemo(() => {
    if (!data?.getAllWidgets) return [];

    return data.getAllWidgets.map((widget, index) => ({
      id: widget.id,
      serialNumber: index + 1,
      name:
        widget.business_details?.display_name ||
        widget.business_details?.name ||
        "Unnamed Widget",
      businessType: getBusinessTypeName(widget.business_details?.business_type),
      websiteUrl: widget.business_details?.website_url || "",
      scriptId: widget.script_id,
      status: "Active",
      // Include full widget data for card view
      ...widget,
    }));
  }, [data]);

  // Handler for single delete - defined before columns memo
  const handleSingleDelete = useCallback((widgetId) => {
    setDeleteTargetId(widgetId);
    setSingleDeleteDialog(true);
  }, []);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        field: "serialNumber",
        headerName: "S.No",
        width: 70,
        sortable: false,
        filterable: false,
      },
      {
        field: "name",
        headerName: "Widget Name",
        flex: 1,
        renderCell: (params) => (
          <Box>
            <Typography variant="subtitle2" fontWeight="medium">
              {params.row.name}
            </Typography>
          </Box>
        ),
      },
      {
        field: "businessType",
        headerName: "Business Type",
        width: 150,
        renderCell: (params) => (
          <Chip
            label={params.row.businessType}
            size="small"
            color="secondary"
            variant="outlined"
          />
        ),
      },
      {
        field: "websiteUrl",
        headerName: "Website URL",
        width: 200,
        renderCell: (params) =>
          params.row.websiteUrl ? (
            <Box display="flex" alignItems="center">
              <WebsiteIcon
                sx={{ fontSize: 16, mr: 1, color: "text.secondary" }}
              />
              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                {params.row.websiteUrl}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.row.status}
            size="small"
            color="success"
            sx={{ minWidth: 60 }}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box display="flex" gap={0.5}>
            <Tooltip title="Edit Widget">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/widget/add-channel-connect?id=${
                    params.row.id
                  }${spaceId ? `&spaceId=${spaceId}` : ""}`;
                }}
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.light",
                    color: "white",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="AI Bot">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/widget/bot-configuration?widgetId=${
                    params.row.id
                  }${spaceId ? `&spaceId=${spaceId}` : ""}`;
                }}
                sx={{
                  color: "secondary.main",
                  "&:hover": {
                    backgroundColor: "secondary.light",
                    color: "white",
                  },
                }}
              >
                <BotIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Widget">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSingleDelete(params.row.id);
                }}
                sx={{
                  color: "error.main",
                  "&:hover": { backgroundColor: "error.light", color: "white" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [spaceId, handleSingleDelete]
  );

  // Search fields configuration
  const searchFields = ["name", "businessType", "websiteUrl", "scriptId"];

  // Event handlers
  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setPage(0); // Reset to first page when searching
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = processedWidgets.map((widget) => widget.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBulkEdit = () => {
    if (selected.length !== 1) {
      setAlert({
        open: true,
        message: "Please select exactly one widget to edit",
        severity: "warning",
      });
      return;
    }

    const widgetId = selected[0];
    window.location.href = `/widget/add-channel-connect?id=${widgetId}${
      spaceId ? `&spaceId=${spaceId}` : ""
    }`;
  };

  const handleBulkDelete = () => {
    if (selected.length > 0) {
      setBulkDeleteDialog(true);
    }
  };

  const handleSingleDeleteConfirm = async () => {
    try {
      await deleteWidget({ variables: { id: deleteTargetId } });
      setAlert({
        open: true,
        message: "Widget deleted successfully",
        severity: "success",
      });
      setSelected(selected.filter((id) => id !== deleteTargetId));
    } catch (error) {
      console.error("Error deleting widget:", error);
      setAlert({
        open: true,
        message: "Error deleting widget",
        severity: "error",
      });
    } finally {
      setSingleDeleteDialog(false);
      setDeleteTargetId(null);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const result = await deleteWidgets({ variables: { ids: selected } });
      setAlert({
        open: true,
        message: `${selected.length} widgets deleted successfully`,
        severity: "success",
      });
      setSelected([]);
    } catch (error) {
      console.error("Error deleting widgets:", error);
      setAlert({
        open: true,
        message: "Error deleting widgets",
        severity: "error",
      });
    } finally {
      setBulkDeleteDialog(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Handle no space selected
  if (!spaceId) {
    return (
      <Paper>
        <Box p={3} textAlign="center">
          <Typography color="error">
            No space selected. Please select a space to view widgets.
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Handle loading state
  if (loading) {
    return (
      <Paper>
        <Box p={3} textAlign="center">
          <Typography>Loading widgets...</Typography>
        </Box>
      </Paper>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Paper>
        <Box p={3} textAlign="center">
          <Typography color="error">
            Error loading widgets: {error.message}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <React.Fragment>
      <Paper>
        <ReusableTable
          data={processedWidgets}
          columns={columns}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search widgets..."
          searchFields={searchFields}
          selected={selected}
          onSelectAllClick={handleSelectAllClick}
          onSelect={handleClick}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          viewStyle={viewStyle}
          setViewStyle={setViewStyle}
          hasCardView={true}
          CardComponent={WidgetCard}
          cardProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
          onBulkEdit={handleBulkEdit}
          onBulkDelete={handleBulkDelete}
          customActions={[]}
          title=""
          showSearch={false}
          // Pass additional props for card component
          cardAdditionalProps={{
            onDelete: handleSingleDelete,
            spaceId: spaceId,
          }}
        />
      </Paper>

      {/* Single Delete Dialog */}
      <Dialog
        open={singleDeleteDialog}
        onClose={() => setSingleDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this widget? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSingleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete Widget
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        open={bulkDeleteDialog}
        onClose={() => setBulkDeleteDialog(false)}
      >
        <DialogTitle>Confirm Bulk Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length} selected widgets?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleBulkDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete {selected.length} Widgets
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

function ChannelConnect() {
  const location = useLocation();
  const { currentSpace } = useSpace();
  const [open, setOpen] = useState(false);

  // Get spaceId from URL params or use current space
  const queryParams = new URLSearchParams(location.search);
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;

  return (
    <React.Fragment>
      <Helmet title="Channel Connect List" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Channel Connect List
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to={`/spaces/${currentSpaceId}`}>
              {currentSpace?.name || "Spaces"}
            </Link>
            <Link
              component={NavLink}
              to={`/widget/dashboard?spaceId=${currentSpaceId}`}
            >
              Dashboard
            </Link>
            <Typography>Channel Connect List</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button
              variant="contained"
              component={Link}
              color="primary"
              to={`/widget/add-channel-connect?spaceId=${currentSpaceId}`}
            >
              <AddIcon />
              New Channel Connect
            </Button>
          </div>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <EnhancedTable />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default ChannelConnect;
